"use client"

import Image from "next/image"
import Link from "next/link"

interface CardData {
  title: string
  benefits: string[]
  buttonText: string
  href?: string             // optional destination for the card CTA
  visible?: boolean          // optional control for enseigner mode
}

const partnerCards: CardData[] = [
  {
    title: "Formateur Indépendant",
    benefits: [
      "Déléguez la prospection",
      "Une présence professionnelle sur le web",
      "Des prospects filtrés et motivés",
      "Un tableau de bord clair pour gérer votre activité",
      "Des documents prêts à l'emploi",
      "Un accompagnement personnalisé à votre démarrage",
    ],
    buttonText: "Je suis formateur",
  },
  {
    title: "Centre de Formation",
    benefits: [
      "Déléguez la prospection",
      "Optimisez vos sessions",
      "Une mise en avant automatique de vos formations",
      "Achetez des leads à la carte",
      "Suivez tous vos formateurs",
      "Profitez d’outils de suivi qualité",
      "Centralisez vos leads & paiements",
    ],
    buttonText: "Je représente un centre",
    href: "/organisme-de-formation",
  },
  {
    title: "Autre organisme",
    benefits: [
      "Une plateforme tout-en-un",
      "Des leads prêts à l'emploi",
      "Une mise en avant automatique de vos formations",
      "Une présence professionnelle sur le web",
      "Un tableau de bord clair pour gérer votre activité",
      "Des documents prêts à l’emploi",
    ],
    buttonText: "Je représente un autre organisme",
    href: "/organisme-de-formation",
    visible: false, // will be overridden by logic if needed
  },
]

export default function TrainingSection({ enseigner = false }: { enseigner?: boolean }) {
  // Filter visible cards
  const visibleCards = partnerCards.filter(
    (card) => card.visible !== false || !enseigner // show "autre" only when !enseigner
  )

  return (
    <section className="w-full bg-linear-to-b from-[#FFBC41]/60 to-[#FF5758]/60 px-4 pt-12.25 pb-12 sm:pb-18.25">
      <div className="mx-auto max-w-321">
        {/* Heading */}
        <h2
          className={`${
            enseigner && "hidden"
          } mb-4 sm:mb-7.5 text-center font-red-hat text-[40px] leading-13.25 font-light text-[#1B365E] tracking-[0.2px]`}
        >
          Vous souhaitez devenir partenaire Spot formations ?
        </h2>

        <h2 className="mb-7.5 text-center font-red-hat text-[30px] sm:text-[40px] leading-9 sm:leading-13.25 font-light text-[#1B365E] tracking-[0.2px]">
          Vous êtes :
        </h2>

        {/* Cards */}
        <div
          className={`flex flex-wrap justify-center mb-6 md:mb-7.5 ${
            enseigner ? "gap-6 md:gap-20" : "gap-6 md:gap-9"
          }`}
        >
          {visibleCards.map((card, idx) => (
            <div
              key={idx}
              className="flex flex-col justify-between rounded-lg bg-white border border-[#FFBC42] px-5 pt-4 pb-6 w-full sm:max-w-75"
            >
              <div>
                <h3 className="mb-2 text-[20px] leading-7.5 tracking-[0.2px] font-medium text-[#1B365E]">
                  {card.title}
                </h3>

                <p className="mb-2 text-[12px] leading-5 font-normal text-[#1B365E]">
                  Ce que Spot Formations vous apporte :
                </p>

                <ul className="space-y-0.5 list-disc list-outside text-[12px] leading-5 tracking-[0.2px] text-[#1D1D1D] ps-4.5">
                  {card.benefits.map((benefit, i) => (
                    <li key={i}>{benefit}</li>
                  ))}
                </ul>
              </div>

              <div className="mt-4 pt-3.75 border-t border-[#D9D9D9] flex items-center justify-center sm:max-w-60 mx-auto">
                {card.href ? (
                  <Link
                    href={card.href}
                    title={card.buttonText}
                    className="flex items-center justify-center gap-2 text-[15px] leading-4.5 hover:underline font-bold text-[#1B365E] cursor-pointer"
                  >
                    <Image
                      src="/assets/icons/arrow-right-circle.svg"
                      alt="arrow right"
                      width={16}
                      height={16}
                    />
                    {card.buttonText}
                  </Link>
                ) : (
                  <button className="flex items-center justify-center gap-2 text-[15px] leading-4.5 hover:underline font-bold text-[#1B365E] cursor-pointer">
                    <Image
                      src="/assets/icons/arrow-right-circle.svg"
                      alt="arrow right"
                      width={16}
                      height={16}
                    />
                    {card.buttonText}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom text */}
        <p className="text-center text-[14px] sm:text-[20px] leading-5 sm:leading-7.5 font-medium text-[#1D1D1D]">
          Rejoignez Spot Formations <br className="md:hidden block" /> et développez votre activité de
          formation.
        </p>
      </div>
    </section>
  )
}